// Copyright (c) 2026 Michael F. Kamprath
//
// engine.js -- Howler.js-backed audio engine for Ewe Can Count.
//
// Responsibilities:
//   - Unlock the Web Audio context on the first user gesture (iOS requirement)
//   - Preload commonly-used sounds with a shared cache
//   - Play one-shot SFX with a Promise that resolves when playback ends
//   - Play background music loops with fade-in/out helpers
//   - Global stop on visibilitychange-style interruptions
//
// The engine intentionally does NOT embed game-specific sequencing logic.
// Callers use plain async/await to sequence sounds (see conversion plan
// section 8).  Cancellation is handled via a PlaybackToken that game code
// checks after each await boundary.

import { Howl, Howler } from 'howler';

// --- Cache of loaded Howl instances, keyed by src URL ----------------------

const cache = new Map();

/**
 * Set of src URLs whose Howl emitted `loaderror` at any point (typically
 * during the eager preload triggered by `new Howl({ preload: true })`).
 *
 * Why this exists: `play()` subscribes to `loaderror` via `.once()` so a
 * file that 404s during a play call still resolves the Promise.  But
 * Howler emits the load error during construction -- BEFORE play() runs
 * -- so `.once('loaderror')` set inside play() never fires.  That left
 * `await play(missingUrl)` hanging forever, which is what wedged the
 * Quiz button when zh-CN was missing letspracticecounting.mp3.
 *
 * This Set is populated by a permanent `.on('loaderror')` listener
 * attached at construction time, and consumed by play() to short-circuit
 * to a resolved Promise when the file is known to be unavailable.
 */
const failedSrcs = new Set();

/**
 * Get (or lazily construct) a cached Howl for a given src URL.
 *
 * @param {string} src
 * @param {{ loop?: boolean, volume?: number, html5?: boolean }} [opts]
 * @returns {Howl}
 */
function getHowl(src, opts = {}) {
  const key = src;
  if (cache.has(key)) return cache.get(key);
  const h = new Howl({
    src: [src],
    loop: !!opts.loop,
    volume: opts.volume ?? 1.0,
    html5: !!opts.html5,
    preload: true,
  });
  // Persistent (non-once) loaderror listener: capture failures that
  // happen during the eager preload at construction time, so subsequent
  // play() calls can short-circuit instead of hanging.
  h.on('loaderror', () => { failedSrcs.add(key); });
  cache.set(key, h);
  return h;
}

// --- Unlock ---------------------------------------------------------------

let unlocked = false;

/**
 * Call from within a user-gesture handler (pointerdown/click/keydown) to
 * unlock the Web Audio context on iOS Safari and other strict browsers.
 *
 * Safari (desktop and iOS) keeps the AudioContext in "suspended" state
 * until a user gesture resumes it.  ctx.resume() alone is NOT sufficient
 * on iOS — the audio output hardware only wakes up when a BufferSource
 * is started inside the user-gesture call stack.  We play a 1-frame
 * silent buffer to satisfy that requirement, matching the canonical
 * unlock pattern used by the I Hear Ewe web app.
 *
 * The silent-buffer start() MUST run synchronously in the gesture frame,
 * before any await.  The subsequent ctx.resume() is fire-and-forget.
 *
 * Safe to call multiple times.
 */
export function unlock() {
  if (unlocked) return;
  unlocked = true;
  try {
    // Safari preserves a "tainted" AudioContext across page refreshes
    // (via bfcache or internal caching).  If the context was created
    // outside a user gesture (e.g. during Howler's module init), Safari
    // remembers it was never properly unlocked and blocks audio output
    // even though ctx.state reports "running".  Only a full browser
    // quit clears this.
    //
    // Observable symptom: engine.js's [audio FIRST] debug log shows
    // `ctx.state=running` and `ctxAdv` advancing (the context clock
    // IS ticking), but no sound is audible.  Verified on Safari 26.4
    // (macOS 26.4, 2026-04-19).  Fix is `⌘Q` on Safari, not `⌘⇧R` --
    // a hard reload does NOT clear the tainted context.  This is
    // primarily a dev-workflow annoyance during heavy rebuild churn;
    // real users rarely hit it because they're not reloading /
    // HMR-ing a page dozens of times in a session.
    //
    // Fix: close the stale context and force a fresh one to be created
    // NOW — while we're on the user-gesture call stack — so Safari
    // treats it as gesture-initiated from the start.
    if (Howler.ctx) {
      try { Howler.ctx.close(); } catch { /* non-fatal */ }
      Howler.ctx = null;
      // Also clear the masterGain so Howler rebuilds the audio graph.
      Howler.masterGain = null;
      // Flush the Howl cache — cached instances hold internal gain
      // nodes from the old context.  If played against the new
      // context they throw InvalidAccessError ("source and
      // destination nodes belong to different audio contexts").
      // Sounds will be lazily re-created by getHowl() when needed.
      for (const h of cache.values()) {
        try { h.unload(); } catch { /* no-op */ }
      }
      cache.clear();
    }
    // Trigger Howler's internal setupAudioContext() to create a fresh
    // context.  The volume() getter calls setupAudioContext() when
    // ctx is null.
    Howler.volume(Howler.volume());

    const ctx = Howler.ctx;
    if (ctx && ctx.state !== 'running') {
      // Play a 1-sample silent buffer synchronously on the gesture
      // stack.  This is what actually wakes the audio hardware on
      // Safari — ctx.resume() alone won't do it.
      try {
        const silent = ctx.createBuffer(1, 1, 22050);
        const src = ctx.createBufferSource();
        src.buffer = silent;
        src.connect(ctx.destination);
        src.start(0);
      } catch {
        /* non-fatal: some test envs or very old browsers may lack these APIs */
      }
      // Now resume the context.  Fire-and-forget — the silent buffer
      // already primed the hardware; resume() just flips the state.
      if (typeof ctx.resume === 'function') {
        ctx.resume().catch(() => { /* non-fatal */ });
      }
    }
  } catch {
    /* Howler.ctx might not exist in some test environments */
  }
}

/** @returns {boolean} */
export function isUnlocked() {
  return unlocked;
}

/**
 * Attempt to resume the Web Audio context after it has been suspended by
 * the browser (e.g. screen lock, tab backgrounding on Safari).
 *
 * Unlike `unlock()`, this is safe to call any number of times and does not
 * require a user gesture on most browsers -- Safari will honour the resume
 * if the original AudioContext was created from a gesture context.
 *
 * Returns a Promise that resolves when the resume completes (or
 * immediately if ctx is already running / unavailable).  Callers that
 * need to be sure the context is running before issuing a play can
 * await it; fire-and-forget callers can ignore the return.
 *
 * Same pattern used in the I Hear Ewe web app: checking ctx.state
 * lazily on each play attempt is more reliable than relying solely on
 * visibilitychange, because Safari doesn't always fire visibilitychange
 * on screen-lock/unlock and because back-forward-cache restores leave
 * the context suspended even after 'visible' has already been dispatched.
 *
 * @returns {Promise<void>}
 */
export async function resume() {
  try {
    const ctx = Howler.ctx;
    if (ctx && typeof ctx.resume === 'function' && ctx.state !== 'running') {
      await ctx.resume();
    }
  } catch {
    /* Howler.ctx might not exist in some test environments */
  }
}

// --- Preload --------------------------------------------------------------

/**
 * How long to wait for the silent warm-play to produce a `play` event
 * before giving up.  AudioContexts that haven't been resumed yet (e.g.
 * preload called before splash dismiss) never emit `play`; the timeout
 * ensures preload doesn't stall indefinitely.
 */
const WARM_PLAY_TIMEOUT_MS = 250;

/**
 * Preload a set of sounds into the cache.
 *
 * Two-stage operation:
 *
 *   1. Wait for Howler's `load` event so the raw audio data is fetched
 *      and decoded into an AudioBuffer.
 *   2. Perform a SILENT (`volume: 0`) play+stop, waiting for the `play`
 *      event, to force Howler to actually construct the
 *      AudioBufferSourceNode, schedule it, and tear it down.
 *
 * The second stage is the important one for latency.  The `load` event
 * alone doesn't exercise the scheduling path; the first real `play()`
 * call still has the overhead of source-node construction and any
 * browser-level audio-graph setup.  By priming with a silent play, the
 * first audible playback during the entrance animation hits an already-
 * warmed pipeline and plays with minimal delay.
 *
 * Resolves when every sound has completed both stages (or hit a
 * loaderror, or its warm-play timeout elapsed without firing `play` --
 * typically because the AudioContext hasn't been unlocked yet).
 *
 * @param {string[]} srcs
 * @returns {Promise<void>}
 */
export function preload(srcs) {
  const promises = srcs.map((src) => new Promise((resolve) => {
    const h = getHowl(src);

    function warmPlay() {
      let resolved = false;
      const finish = () => {
        if (resolved) return;
        resolved = true;
        resolve();
      };
      try {
        // Save and zero out volume so the warm-up is silent.
        const prevVolume = h.volume();
        h.volume(0);
        const id = h.play();
        // Once the source node actually starts playing, the pipeline is
        // primed.  Stop immediately and restore volume.
        h.once('play', () => {
          try { h.stop(id); h.volume(prevVolume); } catch { /* no-op */ }
          finish();
        }, id);
        // Fallback: AudioContext might be suspended (unlock hasn't run
        // yet), in which case `play` never fires.  After the timeout
        // we give up, stop the queued play, and let preload complete so
        // scene-start isn't blocked.
        setTimeout(() => {
          try { h.stop(id); h.volume(prevVolume); } catch { /* no-op */ }
          finish();
        }, WARM_PLAY_TIMEOUT_MS);
      } catch {
        finish();
      }
    }

    if (h.state() === 'loaded') {
      warmPlay();
      return;
    }
    h.once('load', warmPlay);
    // Don't reject on load error -- callers shouldn't block scene start.
    h.once('loaderror', () => resolve());
  }));
  return Promise.all(promises).then(() => undefined);
}

// --- Audio debug instrumentation (dev-only) --------------------------------

/**
 * Enable/disable per-play latency logging.  In production builds this is
 * a no-op stub so callers don't break.
 *
 * @param {boolean} _on
 */
export const setAudioDebugLogging = import.meta.env.DEV
  ? (() => {
      let debugLogging = false;
      const seenSrcs = new Set();
      try {
        if (typeof localStorage !== 'undefined' && localStorage.getItem('audioDebug') === '1') {
          debugLogging = true;
        }
      } catch { /* no-op */ }

      /** @type {(on: boolean) => void} */
      const setter = (on) => { debugLogging = !!on; };
      /** @type {() => boolean} */
      setter._isOn = () => debugLogging;
      setter._seenSrcs = seenSrcs;
      return setter;
    })()
  : (_on) => {};

// --- One-shot playback ----------------------------------------------------

/**
 * Play a one-shot sound.  The returned Promise resolves when playback
 * finishes (`end` event) or on error.  Not rejected on audio errors -- game
 * logic should continue even if a sound file is missing.
 *
 * @param {string} src
 * @param {{ volume?: number }} [opts]
 * @returns {Promise<void>}
 */
export function play(src, opts = {}) {
  // --- Lazy AudioContext resume ------------------------------------------
  // Safari suspends the AudioContext on screen lock, tab backgrounding,
  // and other visibility transitions.  Howler does NOT automatically
  // resume it on subsequent play() calls: it will happily issue a play
  // against a suspended context, which silently queues the sound and
  // never actually makes a sound.  That's exactly the "animations work
  // but audio is silent after screen unlock" bug.
  //
  // Checking ctx.state here and awaiting resume() when suspended fixes
  // the screen-lock case (which the visibilitychange handler in app.js
  // also attempts -- this is a belt-and-suspenders layer because
  // visibilitychange isn't guaranteed to fire on every resume path,
  // especially with bfcache restores).  In the common case ctx is
  // already running and `resume()` returns immediately with no await
  // cost, so this adds no meaningful latency to normal playback.
  const ctx = Howler.ctx;
  const needsResume = !!(ctx && ctx.state && ctx.state !== 'running');

  return new Promise((resolve) => {
    const start = () => {
      doPlay(src, opts, resolve);
    };
    if (needsResume) {
      resume().then(start, start);
    } else {
      start();
    }
  });
}

function doPlay(src, opts, resolve) {
  // Short-circuit if a previous load attempt already failed.  Howler
  // emits `loaderror` during the eager preload at construction time,
  // BEFORE any play() call has had a chance to subscribe via .once();
  // a stale .once('loaderror') added inside this function would never
  // fire for a 404 that was reported earlier.  Without this guard,
  // `await play('/audio/.../missing.mp3')` hangs forever -- the bug
  // that wedged the Quiz button in zh-CN where letspracticecounting.mp3
  // is missing.
  if (failedSrcs.has(src)) {
    resolve();
    return;
  }
  const h = getHowl(src);
  if (typeof opts.volume === 'number') h.volume(opts.volume);

  // Dev-only latency instrumentation: capture timestamps around the
  // play() call so we can measure setup-to-audible latency.
  let debug, tCall, ctxTCall, firstPlay;
  if (import.meta.env.DEV) {
    debug = setAudioDebugLogging._isOn?.();
    tCall = debug ? performance.now() : 0;
    ctxTCall = debug && Howler.ctx ? Howler.ctx.currentTime : 0;
    firstPlay = debug && !setAudioDebugLogging._seenSrcs?.has(src);
    if (debug) setAudioDebugLogging._seenSrcs?.add(src);
  }

  const id = h.play();

  if (import.meta.env.DEV && debug) {
    const onPlay = () => {
      const tPlay = performance.now();
      const ctx = Howler.ctx || {};
      const setupMs = tPlay - tCall;
      const baseMs = (ctx.baseLatency ?? 0) * 1000;
      const outputMs = (ctx.outputLatency ?? 0) * 1000;
      const ctxAdvanceMs = ((ctx.currentTime ?? 0) - ctxTCall) * 1000;
      const floorMs = setupMs + baseMs + outputMs;
      const tag = firstPlay ? 'FIRST' : 'again';
      // eslint-disable-next-line no-console
      console.log(
        `[audio ${tag}] ${src} | setup=${setupMs.toFixed(1)}ms | ` +
        `base=${baseMs.toFixed(1)}ms output=${outputMs.toFixed(1)}ms | ` +
        `ctx.state=${ctx.state} ctxAdv=${ctxAdvanceMs.toFixed(1)}ms | ` +
        `floor=${floorMs.toFixed(1)}ms`,
      );
    };
    h.once('play', onPlay, id);
  }

  const done = () => {
    h.off('end', onEnd, id);
    h.off('stop', onStop, id);
    h.off('playerror', onError, id);
    // loaderror is a Howl-level event with no per-sound id, so the
    // off() call MUST also be id-less or it won't match the listener.
    h.off('loaderror', onLoadError);
    resolve();
  };
  const onEnd = () => done();
  const onStop = () => done();
  const onError = () => done();
  // A missing audio file (HTTP 404) causes Howler to emit `loaderror`,
  // NOT `playerror`.  Without this subscription the Promise never
  // settles, which wedges any `await play(missingUrl)` in the caller's
  // sequence -- previously the symptom was Learn-mode hanging after
  // playLearnSuccess and Quiz-button hanging in zh-CN where
  // letspracticecounting.mp3 isn't shipped.
  //
  // CRITICAL: do NOT pass `id` as the third argument to the loaderror
  // subscription.  end/stop/playerror are per-sound-id events (Howler
  // filters by id when emitted), so passing id to those .once() calls
  // is correct.  But loaderror is a Howl-LEVEL event emitted with no
  // sound id.  When a listener has an id and the event has no id,
  // Howler does not fire the listener -- so an id-filtered loaderror
  // listener would never run, and the failedSrcs short-circuit above
  // only catches subsequent calls (after the persistent on-loaderror
  // listener in getHowl populates failedSrcs).  The first play() call
  // for a missing file would still hang.  Subscribe id-less here.
  const onLoadError = () => done();
  h.once('end', onEnd, id);
  h.once('stop', onStop, id);
  h.once('playerror', onError, id);
  h.once('loaderror', onLoadError);
}

/**
 * Pause for `seconds` using a Promise.  Useful inside async sequence code.
 *
 * @param {number} seconds
 * @returns {Promise<void>}
 */
export function pause(seconds) {
  return new Promise((resolve) => setTimeout(resolve, seconds * 1000));
}

// --- Music loop handle ----------------------------------------------------

/**
 * @typedef {Object} MusicHandle
 * @property {() => void} stop                  Immediately stop (no fade)
 * @property {(seconds: number) => Promise<void>} fadeOut  Fade to 0 then stop
 * @property {(v: number) => void} setVolume    Set current volume (0..1)
 * @property {string} src
 */

/**
 * Start a background music loop.  Returns a handle with stop/fadeOut helpers.
 *
 * @param {string} src
 * @param {{ volume?: number, fadeIn?: number }} [opts]
 * @returns {MusicHandle}
 */
export function playLoop(src, opts = {}) {
  // Same lazy-resume rationale as `play()` above: music that starts
  // while the context is suspended will queue silently.  Fire-and-
  // forget is fine here; the returned MusicHandle doesn't expose a
  // "started" event and the audio will begin as soon as the resume
  // completes (typically within a few ms).
  const ctx = Howler.ctx;
  if (ctx && ctx.state && ctx.state !== 'running') {
    resume();
  }
  const targetVolume = opts.volume ?? 1.0;
  const fadeIn = opts.fadeIn ?? 0;
  const h = getHowl(src, { loop: true });
  h.loop(true);
  if (fadeIn > 0) {
    // Set per-sound volume to 0, NOT the group volume.  Setting group
    // volume (h.volume(0) without an id) would persist across loops
    // and poison subsequent playback.
    const id = h.play();
    h.volume(0, id);
    // Defer the fade until AFTER Howler's playWebAudio has run.
    // playWebAudio calls gain.setValueAtTime(sound._volume) which
    // would cancel any linearRampToValueAtTime that fade() scheduled
    // earlier.  By waiting for the 'play' event (which fires from
    // inside playWebAudio), we ensure the ramp isn't clobbered.
    h.once('play', () => {
      h.fade(0, targetVolume, fadeIn * 1000, id);
    }, id);
    return makeMusicHandle(h, id, src);
  }
  h.volume(targetVolume);
  const id = h.play();
  return makeMusicHandle(h, id, src);
}

/**
 * Play a one-shot with a cancellable handle.  Unlike `play()` (which
 * returns a Promise that resolves on `end` but offers no way to stop
 * or fade playback early), this returns the same shape as
 * `playLoop()` -- stop / fadeOut / setVolume -- but with `loop: false`.
 *
 * Used by the bee mini-game's touch sound: play FlightOfBumbleBeeRims
 * one-shot at mini-game start, fade it out with 2 s decay when the
 * bee exits the viewport.  The legacy FlyingObjectManager.m:playTouchSound
 * uses fadeSoundOnCancel = YES with fadeDuration = 2.0 s, which this
 * API mirrors exactly.
 *
 * Caveat on the Howl cache: `getHowl(src, {loop:false})` creates the
 * Howl with loop=false on first call.  If the same src was preloaded
 * earlier via `playLoop()`, the cached Howl has loop=true already --
 * this function calls `h.loop(false, id)` per-sound-id so a
 * loop-created Howl still plays non-looped here.  Callers that
 * share a src across loop + one-shot modes are rare in practice
 * but the guard costs nothing.
 *
 * @param {string} src
 * @param {{ volume?: number }} [opts]
 * @returns {MusicHandle}
 */
export function playCancellable(src, opts = {}) {
  // Same lazy-resume rationale as play() / playLoop().  The bee
  // mini-game always starts from a user tap (gesture), so the
  // context is almost certainly already running -- this is
  // belt-and-suspenders.
  const ctx = Howler.ctx;
  if (ctx && ctx.state && ctx.state !== 'running') {
    resume();
  }
  const targetVolume = opts.volume ?? 1.0;
  const h = getHowl(src, { loop: false });
  h.volume(targetVolume);
  const id = h.play();
  // Force per-id loop flag off so a cached loop-Howl still plays
  // once here.  Safe no-op when the Howl was created with loop:false.
  try { h.loop(false, id); } catch { /* older Howler versions */ }
  return makeMusicHandle(h, id, src);
}

function makeMusicHandle(h, id, src) {
  let stopped = false;
  return {
    src,
    stop() {
      if (stopped) return;
      stopped = true;
      h.stop(id);
    },
    fadeOut(seconds) {
      if (stopped) return Promise.resolve();
      stopped = true;
      return new Promise((resolve) => {
        const ms = Math.max(1, seconds * 1000);
        const from = h.volume(id);
        h.once('fade', () => {
          h.stop(id);
          resolve();
        }, id);
        h.fade(from, 0, ms, id);
      });
    },
    setVolume(v) {
      if (!stopped) h.volume(v, id);
    },
  };
}

// --- Global controls ------------------------------------------------------

/** Stop every currently-playing sound managed by Howler. */
export function stopAll() {
  try {
    Howler.stop();
  } catch {
    /* no-op */
  }
}

/** Mute/unmute everything (for visibility change etc). */
export function setMuted(muted) {
  try {
    Howler.mute(!!muted);
  } catch {
    /* no-op */
  }
}

// --- Cancellation token ---------------------------------------------------

/**
 * Monotonically-increasing playback token.  Game sequences capture a token
 * at the start and check `token.cancelled` after each await to bail out of
 * zombie audio after a scene change or rapid re-tap.
 */
export class PlaybackToken {
  constructor() {
    /** @type {boolean} */
    this.cancelled = false;
    /** @type {boolean} */
    this.fastForwarded = false;
    /** @type {Set<(token: PlaybackToken) => void>} */
    this._listeners = new Set();
  }
  cancel() {
    if (this.cancelled) return;
    this.cancelled = true;
    this._emit();
  }
  fastForward() {
    if (this.cancelled || this.fastForwarded) return;
    this.fastForwarded = true;
    this._emit();
  }
  onChange(listener) {
    if (typeof listener !== 'function') return () => {};
    this._listeners.add(listener);
    return () => {
      this._listeners.delete(listener);
    };
  }
  _emit() {
    for (const listener of [...this._listeners]) {
      try { listener(this); } catch { /* non-fatal */ }
    }
  }
}

// --- Test-only hooks ------------------------------------------------------

/** @private -- clears the Howl cache and resets unlock state. */
export function __resetForTests() {
  for (const h of cache.values()) {
    try { h.unload(); } catch { /* no-op */ }
  }
  cache.clear();
  failedSrcs.clear();
  unlocked = false;
}

/** @private -- internal cache accessor for test assertions. */
export function __cacheSize() {
  return cache.size;
}
